// טבלה (11, 2026-10-15): מיון, חיפוש וסינון חכם כמו ב-Excel — מקור אחד לבלוק table בדף המארח (TableBlock) ול-ab.table
// ב-iframe של בלוק app. חלק טהור (בלי DOM): זיהוי סוג הנתון מהערכים (inferKind/kindOf), ערכים ייחודיים עם ספירה שתלויה
// בשאר הסינונים (distinctValues), viewIdx / chips / totals — נבדק ב-vitest. חלק DOM: createTableControls (שורת חיפוש, שבבים,
// לוח סינון לעמודה: רשימת ערכים + תנאי לפי סוג) — משותף לשניהם; createTable — ab.table. ה-import מוסר בהזרקה ל-iframe (app-doc).
import { normalize } from './series.js';

/** כל המחרוזות של הטבלה (11 §6: בלי מסגרת i18n). */
export const TABLE_HE = {
  search: 'חיפוש בטבלה',
  rows: (n) => `${n.toLocaleString('he-IL')} שורות`,
  shownOf: (shown, total) => `${shown.toLocaleString('he-IL')} מתוך ${total.toLocaleString('he-IL')} שורות`,
  clearAll: 'נקה הכל',
  filterBy: (label) => `סינון: ${label}`,
  close: 'סגור',
  done: 'אישור',
  remove: (text) => `הסר סינון: ${text}`,
  empty: '(ריקים)',
  contains: 'מכיל',
  starts: 'מתחיל ב',
  notContains: 'לא מכיל',
  from: 'מ־',
  to: 'עד',
  findValue: 'חיפוש ערך',
  selectAll: 'בחר הכל',
  selectFound: 'בחר את כל התוצאות',
  clear: 'נקה',
  clearFilter: 'נקה סינון',
  values: (n) => `${n.toLocaleString('he-IL')} ערכים`,
  selectedOf: (n, m) => `${n.toLocaleString('he-IL')} מתוך ${m.toLocaleString('he-IL')} נבחרו`,
  more: (n) => `עוד ${n.toLocaleString('he-IL')} ערכים. חפש כדי לצמצם`,
  showMore: 'הצג עוד',
  noValues: 'אין ערכים מתאימים',
  byValue: 'לפי ערך',
  byCount: 'לפי כמות',
  orderBy: 'סדר הרשימה',
  tabValues: 'ערכים',
  tabCond: { num: 'טווח', date: 'תאריכים', text: 'תנאי' },
  condition: 'תנאי',
  value: 'ערך',
  yes: 'כן',
  no: 'לא',
  ops: { between: 'בין', eq: 'שווה ל', ne: 'שונה מ', gt: 'גדול מ', lt: 'קטן מ', ge: 'גדול או שווה ל', le: 'קטן או שווה ל' },
  presets: { today: 'היום', week: 'השבוע', month: 'החודש', prevMonth: 'החודש הקודם', year: 'השנה', last30: '30 ימים אחרונים' },
  rangeHint: (lo, hi) => `בנתונים: \u2066${lo}\u2069 עד \u2066${hi}\u2069`,
  found: (n) => `נמצאו ${n.toLocaleString('he-IL')}`,
  expand: (label) => `הרחב ${label}`,
  sortedBy: (label, dir) => `ממוין לפי ${label} (${dir === 'asc' ? 'עולה' : 'יורד'})`,
  total: 'סה״כ',
  noMatch: 'אין שורות שמתאימות לסינון',
};

/** מכמה שורות מוצגים חיפוש וסינון (טבלה קטנה — רק מיון). */
export const CONTROLS_MIN_ROWS = 10;
/** כמה ערכים מציירים ברשימת הסינון בכל פעם (השאר — "הצג עוד" או חיפוש ברשימה). */
export const LIST_PAGE = 300;
/** כמה מהערכים שאינם ריקים חייבים להתפרש כדי שהעמודה תיחשב מספר/תאריך/כן-לא. */
export const INFER_MIN = 0.95;
/** טווחי תאריכים מהירים (מחושבים בכל פעם מחדש — "החודש" נשאר החודש גם ביצירה חיה). */
export const DATE_PRESETS = ['today', 'week', 'month', 'prevMonth', 'year', 'last30'];

const NUMERIC_TYPES = ['number', 'currency', 'percent', 'compact'];
const isEmpty = (v) => v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
const isNum = (x) => typeof x === 'number' && Number.isFinite(x);
const COLL = new Intl.Collator('he', { numeric: true, sensitivity: 'base' });
const DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jerusalem', year: 'numeric', month: '2-digit', day: '2-digit' });
const MONTH_HE = new Intl.DateTimeFormat('he-IL', { month: 'long', timeZone: 'UTC' });
const NF = new Intl.NumberFormat('he-IL', { maximumFractionDigits: 6 });

/** סוג ההשוואה לפי הטיפוס המוצהר בלבד: num / date / text (id = text עם השוואה מספרית של ספרות — "7" לפני "10"). */
export function colKind(type) {
  if (NUMERIC_TYPES.includes(type)) return 'num';
  if (type === 'date' || type === 'datetime') return 'date';
  return 'text';
}

// ───────────────────────── פענוח ערכים ─────────────────────────

const NUM_BODY = /^(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?$|^\.\d+$/;
/** קוד שנראה כמו מספר אבל אינו מספר: אפסים מובילים ("00123") או יותר מ-15 ספרות (טלפון, ח"פ ארוך, ברקוד). */
export function isCodeLike(v) {
  if (typeof v !== 'string') return false;
  const s = v.trim();
  return /^[-+]?0\d+$/.test(s) || /^\d{16,}$/.test(s);
}

/**
 * מספר מערך: number, או מחרוזת "1,234.5" / "-12" / "12.5%" / "₪ 1,200" / "1,200 ₪" / "(300)" / "300-" (מינוס בסוף, כמו ב-ERP).
 * strict: קוד עם אפסים מובילים אינו מספר (לזיהוי סוג); אחוז נשאר ביחידות שנכתבו (12.5% → 12.5).
 */
export function parseNumber(v, strict = false) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string') return null;
  let s = v.replace(/[\s\u00a0\u200e\u200f‪-‮]/g, '');
  if (s === '') return null;
  let neg = false;
  if (s[0] === '(' && s[s.length - 1] === ')') {
    neg = true;
    s = s.slice(1, -1);
  }
  if (/^[-−]/.test(s)) {
    neg = !neg;
    s = s.slice(1);
  } else if (s[0] === '+') s = s.slice(1);
  s = s.replace(/^[₪$€]|[₪$€%]$/g, '');
  if (/^[-−]/.test(s)) {
    neg = !neg;
    s = s.slice(1);
  }
  if (s.endsWith('-')) {
    neg = !neg;
    s = s.slice(0, -1);
  }
  if (!NUM_BODY.test(s)) return null;
  if (strict && (/^0\d/.test(s) || /^\d{16,}$/.test(s))) return null;
  const n = Number(s.replace(/,/g, ''));
  if (!Number.isFinite(n)) return null;
  return neg ? -n : n;
}

const ISO_RE = /^(\d{4})-(\d{2})(?:-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?)?$/;
const DMY_RE = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4}|\d{2})(?:[ T,]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/;
const pad2 = (n) => String(n).padStart(2, '0');
const validDay = (y, m, d) => {
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
};

/**
 * תאריך מערך: Date, YYYY-MM, YYYY-MM-DD, datetime ISO (עם/בלי אזור זמן), dd/mm/yyyy, dd.mm.yy, dd-mm-yyyy [hh:mm].
 * → {day: 'YYYY-MM-DD' (יום קלנדרי בישראל; בלי אזור זמן — כפי שנכתב), t: ms למיון, month: true ל-YYYY-MM} | null.
 * מספר אינו תאריך (חוץ ממוצהר). dd/mm — הסדר הישראלי; 31/12 תקין, 12/31 לא.
 */
export function parseDate(v) {
  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isNaN(t) ? null : { day: DAY.format(t), t };
  }
  if (typeof v !== 'string') return null;
  const s = v.trim();
  let m = ISO_RE.exec(s);
  if (m) {
    const y = +m[1];
    const mo = +m[2];
    const d = m[3] ? +m[3] : 1;
    if (!validDay(y, mo, d)) return null;
    if (m[7]) {
      const t = Date.parse(s.replace(' ', 'T'));
      return Number.isNaN(t) ? null : { day: DAY.format(t), t };
    }
    const day = `${m[1]}-${m[2]}-${pad2(d)}`;
    const t = Date.UTC(y, mo - 1, d, m[4] ? +m[4] : 12, m[5] ? +m[5] : 0, m[6] ? +m[6] : 0);
    return m[3] ? { day, t } : { day, t, month: true };
  }
  m = DMY_RE.exec(s);
  if (m) {
    let y = +m[3];
    if (m[3].length === 2) y += y < 70 ? 2000 : 1900;
    const mo = +m[2];
    const d = +m[1];
    if (!validDay(y, mo, d)) return null;
    if (m[4] && (+m[4] > 23 || +m[5] > 59)) return null;
    return { day: `${y}-${pad2(mo)}-${pad2(d)}`, t: Date.UTC(y, mo - 1, d, m[4] ? +m[4] : 12, m[4] ? +m[5] : 0, m[6] ? +m[6] : 0) };
  }
  return null;
}

const BOOL_WORDS = { true: true, false: false, yes: true, no: false, y: true, n: false, כן: true, לא: false };
/** כן/לא מערך: true/false, "true"/"false", "כן"/"לא", "Y"/"N", "yes"/"no" (1/0 נשארים מספר). */
export function parseBool(v) {
  if (typeof v === 'boolean') return v;
  if (typeof v !== 'string') return null;
  const k = v.trim().toLowerCase();
  return k in BOOL_WORDS ? BOOL_WORDS[k] : null;
}

/** זמן (ms) של ערך תאריך (למיון); מספר = ms. */
export function timeOf(v) {
  if (isEmpty(v)) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const d = parseDate(v);
  if (d) return d.t;
  if (typeof v !== 'string') return null;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
}

/** היום הקלנדרי (YYYY-MM-DD, שעון ישראל) של ערך תאריך — לסינון לפי טווח. */
export function dayOf(v) {
  if (isEmpty(v)) return null;
  const d = parseDate(v);
  if (d) return d.day;
  const t = timeOf(v);
  return t === null ? null : DAY.format(t);
}

/**
 * סוג הנתון מהערכים עצמם: 'bool' | 'num' | 'date' | 'text'. ריקים לא נספרים; ≥ INFER_MIN מהשאר צריכים להתפרש.
 * קוד עם אפסים מובילים ("00123") או ארוך מ-15 ספרות → כל העמודה טקסט (קודי פריט אינם מספרים). דגימה עד 20,000 ערכים.
 */
export function inferKind(values) {
  const n = values.length;
  const step = Math.max(1, Math.ceil(n / 20000));
  let seen = 0;
  let nums = 0;
  let dates = 0;
  let bools = 0;
  for (let i = 0; i < n; i += step) {
    const v = values[i];
    if (isEmpty(v)) continue;
    seen++;
    if (typeof v === 'boolean') {
      bools++;
      continue;
    }
    if (typeof v === 'string' && isCodeLike(v)) return 'text';
    if (parseBool(v) !== null) bools++;
    else if (parseNumber(v, true) !== null) nums++;
    else if (typeof v !== 'number' && parseDate(v)) dates++;
  }
  if (!seen) return 'text';
  if (bools / seen >= INFER_MIN) return 'bool';
  if (nums / seen >= INFER_MIN) return 'num';
  if (dates / seen >= INFER_MIN) return 'date';
  return 'text';
}

// ───────────────────────── מידע לעמודה (נשמר לכל rows) ─────────────────────────

const INFO = new WeakMap();
const NUM_KEY = /^-?\d+(\.\d+)?(e[-+]?\d+)?$/;
const infoKey = (col) => `${col.key}\u0001${col.type || ''}\u0001${col.descKey || ''}`;

/**
 * הסוג האפקטיבי של עמודה: טיפוס מוצהר שאינו טקסט קובע (number/currency/percent → num, date/datetime → date, id → text);
 * text / חסר / לא מוכר → לפי הערכים (inferKind). rows — כדי לזהות; בלי rows — לפי ההצהרה בלבד.
 */
export function kindOf(col, rows) {
  if (!col) return 'text';
  if (NUMERIC_TYPES.includes(col.type)) return 'num';
  if (col.type === 'date' || col.type === 'datetime') return 'date';
  if (col.type === 'id' || !rows) return 'text';
  return colInfo(rows, col).kind;
}

/** פעם אחת לכל (rows, עמודה): הסוג, מפתח קבוצה לכל שורה (ערך מנורמל; '' = ריק), ערך השוואה, ושורה ראשונה לכל מפתח. */
function colInfo(rows, col) {
  let per = INFO.get(rows);
  if (!per) {
    per = new Map();
    INFO.set(rows, per);
  }
  const k = infoKey(col);
  let info = per.get(k);
  if (info) return info;
  const n = rows.length;
  const raw = new Array(n);
  for (let i = 0; i < n; i++) raw[i] = rows[i] ? rows[i][col.key] : undefined;
  let kind;
  if (NUMERIC_TYPES.includes(col.type)) kind = 'num';
  else if (col.type === 'date' || col.type === 'datetime') kind = 'date';
  else if (col.type === 'id') kind = 'text';
  else kind = inferKind(raw);
  const keys = new Array(n);
  const vals = new Array(n);
  const first = new Map();
  let months = 0;
  for (let i = 0; i < n; i++) {
    const v = raw[i];
    let key = '';
    let val = null;
    if (!isEmpty(v)) {
      if (kind === 'num') {
        val = parseNumber(v);
        key = val === null ? String(v).trim() : String(val);
      } else if (kind === 'date') {
        const d = typeof v === 'number' ? { day: DAY.format(v), t: v } : parseDate(v);
        if (d) {
          val = d.t;
          key = d.day;
          if (d.month) months++;
        } else key = String(v).trim();
      } else if (kind === 'bool') {
        const b = parseBool(v);
        val = b === null ? null : b ? 1 : 0;
        key = b === null ? String(v).trim() : b ? 'true' : 'false';
      } else {
        key = typeof v === 'object' ? JSON.stringify(v) : String(v).trim();
        val = key;
      }
    }
    keys[i] = key;
    vals[i] = val;
    if (!first.has(key)) first.set(key, i);
  }
  info = { kind, raw, keys, vals, first, monthly: kind === 'date' && months > 0, order: null, labels: new Map(), norm: null };
  per.set(k, info);
  return info;
}

/** תווית ערך כפי שהוא מוצג: מוצהר → fmt; תאריך → dd.mm.yyyy; כן/לא; קוד · תיאור; אחרת — הערך כפי שהופיע ראשון. */
function labelOf(info, col, key, fmt, rows) {
  let l = info.labels.get(key);
  if (l !== undefined) return l;
  const i = info.first.get(key);
  const v = i === undefined ? key : info.raw[i];
  if (key === '') l = TABLE_HE.empty;
  else if (info.kind === 'bool' && (key === 'true' || key === 'false')) l = key === 'true' ? TABLE_HE.yes : TABLE_HE.no;
  else if (info.kind === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(key)) {
    const d = i === undefined ? null : parseDate(info.raw[i]);
    l = d && d.month ? `${key.slice(5, 7)}/${key.slice(0, 4)}` : fmt ? fmt(key, 'date') : `${key.slice(8)}.${key.slice(5, 7)}.${key.slice(0, 4)}`;
  } else if (info.kind === 'num' && NUMERIC_TYPES.includes(col.type) && NUM_KEY.test(key)) l = fmt ? fmt(Number(key), col.type) : NF.format(Number(key));
  else l = typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v).trim();
  if (col.descKey && key !== '' && i !== undefined && rows && rows[i] && !isEmpty(rows[i][col.descKey])) l = `${l} · ${rows[i][col.descKey]}`;
  info.labels.set(key, l);
  return l;
}

/** סדר הערכים "לפי ערך" (פעם אחת לעמודה): מספרים עולה, תאריכים עולה, טקסט לפי collation עברית, כן לפני לא; ריק בסוף. */
function valueOrder(info, col, fmt, rows) {
  if (info.order) return info.order;
  const keys = [...info.first.keys()];
  const rank = (k) => (k === '' ? 2 : info.vals[info.first.get(k)] === null ? 1 : 0);
  const cmpVal = (a, b) => {
    const x = info.vals[info.first.get(a)];
    const y = info.vals[info.first.get(b)];
    if (info.kind === 'text') return COLL.compare(labelOf(info, col, a, fmt, rows), labelOf(info, col, b, fmt, rows));
    if (info.kind === 'bool') return y - x;
    return x - y;
  };
  keys.sort((a, b) => rank(a) - rank(b) || (rank(a) === 0 ? cmpVal(a, b) : COLL.compare(a, b)));
  info.order = keys;
  return keys;
}

// ───────────────────────── מיון ─────────────────────────

/** כיוון ראשון בלחיצה: מספרים ותאריכים — מהגדול/החדש; טקסט וכן/לא — א→ת. rows — לזיהוי הסוג מהערכים. */
export function firstDir(col, rows) {
  const k = kindOf(col, rows);
  return k === 'num' || k === 'date' ? 'desc' : 'asc';
}

/** מחזור לחיצה על כותרת: כיוון ראשון → הפוך → בלי מיון. */
export function nextSort(sort, col, rows) {
  if (!sort || sort.key !== col.key) return { key: col.key, dir: firstDir(col, rows) };
  if (sort.dir === firstDir(col, rows)) return { key: col.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' };
  return null;
}

/** ממיין אינדקסים של rows (יציב; ריקים תמיד בסוף; לפי הסוג האפקטיבי — מספרים כמספרים גם כשהם מחרוזות). */
export function sortIdx(idx, rows, sort, cols) {
  const out = idx.slice();
  const col = sort && cols.find((c) => c.key === sort.key);
  if (!col) return out;
  const info = colInfo(rows, col);
  const vals = info.vals;
  const text = info.kind === 'text';
  const s = sort.dir === 'desc' ? -1 : 1;
  return out.sort((a, b) => {
    const x = vals[a];
    const y = vals[b];
    if (x === null || y === null) return x === y ? a - b : x === null ? 1 : -1;
    const r = text ? COLL.compare(x, y) : x - y;
    return r ? r * s : a - b;
  });
}

// ───────────────────────── חיפוש וסינון ─────────────────────────

const SEP = ' ¦ ';
/** טקסט לחיפוש לכל שורה (פעם אחת לכל rows): הערך הגולמי + המפורמט + התיאור של עמודת id. */
export function searchIndex(rows, cols, fmt) {
  return rows.map((r) =>
    normalize(
      cols
        .map((c) => {
          const v = r ? r[c.key] : undefined;
          let s = isEmpty(v) ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
          if (!isEmpty(v) && c.type && fmt) s += ` ${fmt(v, c.type)}`;
          if (c.descKey && r && !isEmpty(r[c.descKey])) s += ` ${r[c.descKey]}`;
          return s;
        })
        .join(SEP),
    ),
  );
}
const HAY = new WeakMap();
const hayFor = (rows, cols, fmt) => {
  let h = HAY.get(rows);
  if (!h || h.cols !== cols || h.fmt !== fmt) {
    h = { cols, fmt, hay: searchIndex(rows, cols, fmt) };
    HAY.set(rows, h);
  }
  return h.hay;
};

/** היום הקלנדרי בישראל + חשבון ימים על YYYY-MM-DD. */
const dayAdd = (day, n) => {
  const t = new Date(`${day}T12:00:00Z`);
  t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10);
};
const monthEnd = (y, m) => `${y}-${pad2(m)}-${pad2(new Date(Date.UTC(y, m, 0)).getUTCDate())}`;

/** טווח של קיצור תאריכים (שבוע = ראשון עד שבת, שעון ישראל): {from, to} | null. */
export function presetRange(preset, now = Date.now()) {
  const today = DAY.format(now);
  const y = +today.slice(0, 4);
  const m = +today.slice(5, 7);
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'week': {
      const dow = new Date(`${today}T12:00:00Z`).getUTCDay();
      return { from: dayAdd(today, -dow), to: dayAdd(today, 6 - dow) };
    }
    case 'month':
      return { from: `${y}-${pad2(m)}-01`, to: monthEnd(y, m) };
    case 'prevMonth': {
      const py = m === 1 ? y - 1 : y;
      const pm = m === 1 ? 12 : m - 1;
      return { from: `${py}-${pad2(pm)}-01`, to: monthEnd(py, pm) };
    }
    case 'year':
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    case 'last30':
      return { from: dayAdd(today, -29), to: today };
    default:
      return null;
  }
}

/**
 * האם יש בסינון תנאי בפועל. צורות: {in:[מפתחות]} (רשימת ערכים), {text, op?: contains|starts|not}, {min?, max?} (בין/≥/≤),
 * {cmp: eq|ne|gt|lt, n}, {from?, to?} או {preset} (תאריכים). {in: []} = אין סינון.
 */
export function isActive(f) {
  if (!f || typeof f !== 'object') return false;
  if (Array.isArray(f.in)) return f.in.length > 0;
  if ('text' in f) return normalize(f.text) !== '';
  if ('cmp' in f) return isNum(f.n);
  if ('min' in f || 'max' in f) return isNum(f.min) || isNum(f.max);
  if (f.preset) return DATE_PRESETS.includes(f.preset);
  return !!(f.from || f.to);
}

/** בודק שורה מול סינון של עמודה. info = colInfo; hay = טקסט מנורמל לעמודה (lazy). */
function matcher(rows, col, f, now) {
  const info = colInfo(rows, col);
  if (Array.isArray(f.in)) {
    const set = new Set(f.in);
    return (i) => set.has(info.keys[i]);
  }
  if ('text' in f) {
    const t = normalize(f.text);
    if (!info.norm) {
      info.norm = info.raw.map((v, i) => {
        const d = col.descKey && rows[i] && !isEmpty(rows[i][col.descKey]) ? ` ${rows[i][col.descKey]}` : '';
        return normalize(`${isEmpty(v) ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v)}${d}`);
      });
    }
    const norm = info.norm;
    if (f.op === 'starts') return (i) => norm[i].startsWith(t);
    if (f.op === 'not') return (i) => !norm[i].includes(t);
    return (i) => norm[i].includes(t);
  }
  const numAt = (i) => {
    let n = info.kind === 'num' ? info.vals[i] : parseNumber(info.raw[i]);
    if (n === null) return null;
    if (col.type === 'percent') n = Math.round(n * 100 * 1e9) / 1e9;
    return n;
  };
  if ('cmp' in f) {
    const x = f.n;
    const ops = { eq: (n) => n === x, ne: (n) => n !== x, gt: (n) => n > x, lt: (n) => n < x };
    const op = ops[f.cmp] || ops.eq;
    return (i) => {
      const n = numAt(i);
      return n !== null && op(n);
    };
  }
  if ('min' in f || 'max' in f) {
    return (i) => {
      const n = numAt(i);
      return n !== null && (!isNum(f.min) || n >= f.min) && (!isNum(f.max) || n <= f.max);
    };
  }
  const r = f.preset ? presetRange(f.preset, now) || {} : f;
  return (i) => {
    const d = info.kind === 'date' ? (info.vals[i] === null ? null : info.keys[i]) : dayOf(info.raw[i]);
    return d !== null && (!r.from || d >= r.from) && (!r.to || d <= r.to);
  };
}

/** אינדקסים שעוברים חיפוש + סינונים (בלי מיון). except = מפתח עמודה שהסינון שלה לא נחשב (לספירה "בהינתן שאר הסינונים"). */
export function filterIdx(rows, cols, state, fmt, index, except) {
  const st = state || {};
  const terms = normalize(st.q || '').split(' ').filter(Boolean);
  const now = Date.now();
  const tests = cols.filter((c) => c.key !== except && isActive(st.filters && st.filters[c.key])).map((c) => matcher(rows, c, st.filters[c.key], now));
  const hay = terms.length ? index || hayFor(rows, cols, fmt) : null;
  const keep = [];
  for (let i = 0; i < rows.length; i++) {
    if (hay && !terms.every((t) => hay[i].includes(t))) continue;
    let ok = true;
    for (const t of tests) {
      if (!t(i)) {
        ok = false;
        break;
      }
    }
    if (ok) keep.push(i);
  }
  return keep;
}

/**
 * התצוגה: אינדקסים של rows אחרי סינון לפי עמודות, חיפוש (כל המילים, בכל העמודות) ומיון.
 * state = {q?, filters?: {[key]: filter}, sort?: {key, dir} | null}. index = searchIndex (אופציונלי — לחיסכון).
 */
export function viewIdx(rows, cols, state, fmt, index) {
  return sortIdx(filterIdx(rows, cols, state, fmt, index), rows, state && state.sort, cols);
}

/**
 * הערכים הייחודיים של עמודה עם ספירה — כמו רשימת הסינון של Excel. הספירה היא על השורות שעוברות את החיפוש ושאר הסינונים
 * (לא את הסינון של העמודה עצמה), וערך שאין לו שורות כאלה לא מופיע — אלא אם הוא נבחר כעת. ריק = '' עם התווית "(ריקים)".
 * o: {cols?, state?, fmt?, index?, order?: 'value'|'count'}
 * → {kind, items: [{value, labelHe, count}], rows: מספר השורות שנספרו}
 */
export function distinctValues(rows, col, o = {}) {
  const info = colInfo(rows, col);
  const cols = o.cols || [col];
  const counts = new Map();
  let n;
  if (o.state && (normalize(o.state.q || '') || cols.some((c) => c.key !== col.key && isActive(o.state.filters && o.state.filters[c.key])))) {
    const idx = filterIdx(rows, cols, o.state, o.fmt, o.index, col.key);
    n = idx.length;
    for (const i of idx) {
      const k = info.keys[i];
      counts.set(k, (counts.get(k) || 0) + 1);
    }
  } else {
    n = rows.length;
    for (let i = 0; i < n; i++) {
      const k = info.keys[i];
      counts.set(k, (counts.get(k) || 0) + 1);
    }
  }
  const f = o.state && o.state.filters && o.state.filters[col.key];
  const keep = new Set(f && Array.isArray(f.in) ? f.in : []);
  const order = valueOrder(info, col, o.fmt, rows);
  const items = [];
  for (const k of order) {
    const c = counts.get(k) || 0;
    if (c || keep.has(k)) items.push({ value: k, labelHe: labelOf(info, col, k, o.fmt, rows), count: c });
  }
  for (const k of keep) if (!info.first.has(k)) items.push({ value: k, labelHe: k === '' ? TABLE_HE.empty : k, count: 0 });
  if (o.order === 'count') {
    const pos = new Map(items.map((it, i) => [it.value, i]));
    items.sort((a, b) => b.count - a.count || pos.get(a.value) - pos.get(b.value));
  }
  return { kind: info.kind, items, rows: n };
}

/**
 * בחירה ברשימת הערכים — כמו ב-Excel. sel: Set של מפתחות, או null = הכל מסומן (אין סינון). all = כל המפתחות ברשימה;
 * found = מה שנמצא בחיפוש ברשימה (null = בלי חיפוש). action: 'all' — בלי חיפוש: הכל, עם חיפוש: רק מה שנמצא (מחליף את
 * הבחירה); 'none' — בלי חיפוש: כלום, עם חיפוש: מוריד את מה שנמצא; {key, on} — סימון אחד. → Set | null.
 */
export function pickValues(sel, all, found, action) {
  if (action === 'all') return found ? new Set(found) : null;
  const next = new Set(sel || all);
  if (action === 'none') {
    if (!found) return new Set();
    for (const k of found) next.delete(k);
    return next;
  }
  if (action.on) next.add(action.key);
  else next.delete(action.key);
  return next;
}

/** הסינון שנובע מבחירה: הכל מסומן או כלום → null (בלי סינון); אחרת {in} בסדר הרשימה (ערכים שנבחרו ואינם ברשימה — בסוף). */
export function selectionFilter(sel, all) {
  if (!sel || sel.size === 0 || all.every((k) => sel.has(k))) return null;
  const inList = new Set(all);
  return { in: all.filter((k) => sel.has(k)).concat([...sel].filter((k) => !inList.has(k))) };
}

/** התווית המוצגת של מפתח ערך בעמודה (לשבבים). */
export function valueLabel(rows, col, key, fmt) {
  if (!rows) return key === '' ? TABLE_HE.empty : key;
  return labelOf(colInfo(rows, col), col, key, fmt, rows);
}

/** טווח המספרים בעמודה (ביחידות התצוגה — אחוז כ-12.5) — לרמז בלוח הסינון. */
export function numRange(rows, col) {
  const info = colInfo(rows, col);
  let lo = Infinity;
  let hi = -Infinity;
  for (let i = 0; i < info.vals.length; i++) {
    let n = info.kind === 'num' ? info.vals[i] : parseNumber(info.raw[i]);
    if (n === null) continue;
    if (col.type === 'percent') n = Math.round(n * 100 * 1e9) / 1e9;
    if (n < lo) lo = n;
    if (n > hi) hi = n;
  }
  return lo === Infinity ? null : { min: lo, max: hi };
}

/** יש חיפוש או סינון פעילים (מיון לא נחשב). */
export function isFiltered(state, cols) {
  const st = state || {};
  return normalize(st.q || '') !== '' || cols.some((c) => isActive(st.filters && st.filters[c.key]));
}

/** סכום לעמודות מספר/מטבע מוצהרות (לא אחוז, לא עמודה שזוהתה כמספר — אולי קוד) — על השורות שבתצוגה. */
export function columnTotals(rows, cols) {
  const out = {};
  for (const c of cols) {
    if (colKind(c.type) !== 'num' || c.type === 'percent') continue;
    let s = 0;
    let any = false;
    for (const r of rows) {
      const n = parseNumber(r ? r[c.key] : undefined);
      if (n !== null) {
        s += n;
        any = true;
      }
    }
    if (any) out[c.key] = Math.round(s * 1e6) / 1e6;
  }
  return out;
}

const CMP_SIGN = { eq: '=', ne: '≠', gt: '>', lt: '<' };
/** טקסט השבב של סינון פעיל: "לקוח: א, ב" / "תיאור מכיל ״x״" / "סכום: 100–500" / "סכום: > 100" / "תאריך: החודש". rows — לתוויות ערכים. */
export function chipText(col, f, fmt, rows) {
  const label = col.labelHe || col.key;
  const show = (v, t) => (fmt ? fmt(v, t) : String(v));
  if (Array.isArray(f.in)) {
    const names = f.in.map((v) => valueLabel(rows, col, v, fmt));
    return `${label}: ${names.length <= 2 ? names.join(', ') : TABLE_HE.values(names.length)}`;
  }
  if ('text' in f) {
    const op = f.op === 'starts' ? TABLE_HE.starts : f.op === 'not' ? TABLE_HE.notContains : TABLE_HE.contains;
    return `${label} ${op} ״${String(f.text).trim()}״`;
  }
  const t = NUMERIC_TYPES.includes(col.type) ? col.type : 'number';
  const v = (x) => (NUMERIC_TYPES.includes(col.type) ? show(t === 'percent' ? x / 100 : x, t) : NF.format(x));
  if ('cmp' in f) return `${label}: ${CMP_SIGN[f.cmp] || '='} ${v(f.n)}`;
  if ('min' in f || 'max' in f) {
    if (isNum(f.min) && isNum(f.max)) return `${label}: ${v(f.min)}–${v(f.max)}`;
    return isNum(f.min) ? `${label}: ≥ ${v(f.min)}` : `${label}: ≤ ${v(f.max)}`;
  }
  if (f.preset) return `${label}: ${TABLE_HE.presets[f.preset] || f.preset}`;
  if (f.from && f.to) return `${label}: ${show(f.from, 'date')}–${show(f.to, 'date')}`;
  return f.from ? `${label}: ${TABLE_HE.from}${show(f.from, 'date')}` : `${label}: ${TABLE_HE.to} ${show(f.to, 'date')}`;
}

/** שורת הסבר לייצוא (Excel): "12 מתוך 340 שורות · חיפוש ״x״ · <שבבים> · ממוין לפי …" — ריק כשאין חיפוש/סינון/מיון. */
export function viewNote(state, cols, shown, total, fmt, rows) {
  const st = state || {};
  const parts = [];
  if (isFiltered(st, cols)) parts.push(TABLE_HE.shownOf(shown, total));
  if (normalize(st.q || '')) parts.push(`${TABLE_HE.search} ״${String(st.q).trim()}״`);
  for (const c of cols) if (isActive(st.filters && st.filters[c.key])) parts.push(chipText(c, st.filters[c.key], fmt, rows));
  const sc = st.sort && cols.find((c) => c.key === st.sort.key);
  if (sc) parts.push(TABLE_HE.sortedBy(sc.labelHe || sc.key, st.sort.dir));
  return parts.join(' · ');
}

/** עץ תאריכים לרשימת הסינון (כמו ב-Excel): שנה → חודש → ימים. items = distinctValues(...).items של עמודת תאריך. */
export function dateTree(items) {
  const years = [];
  const rest = [];
  let y = null;
  let m = null;
  for (const it of items) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(it.value)) {
      rest.push(it);
      continue;
    }
    const yk = it.value.slice(0, 4);
    const mk = it.value.slice(0, 7);
    if (!y || y.key !== yk) {
      y = { key: yk, labelHe: yk, count: 0, months: [] };
      years.push(y);
      m = null;
    }
    if (!m || m.key !== mk) {
      m = { key: mk, labelHe: MONTH_HE.format(Date.UTC(+yk, +mk.slice(5) - 1, 15)), count: 0, items: [] };
      y.months.push(m);
    }
    m.items.push(it);
    m.count += it.count;
    y.count += it.count;
  }
  return { years, rest };
}

// ───────────────────────── DOM ─────────────────────────

let tseq = 0;
const tmk = (doc, tag, cls, text) => {
  const e = doc.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};
const tbtn = (doc, cls, text, onClick) => {
  const b = tmk(doc, 'button', cls, text);
  b.type = 'button';
  if (onClick) b.addEventListener('click', onClick);
  return b;
};
/** מספר מקלט המשתמש ("1,200", "12.5%", "-3") — null אם ריק/לא מספר. */
const inputNum = (s) => parseNumber(String(s ?? ''));

/**
 * פקדי הטבלה (DOM, משותף לדף המארח ול-iframe): שורת חיפוש + "N מתוך M" + "נקה הכל", שבבים לסינונים פעילים (× מסיר),
 * ולוח סינון לעמודה (נפתח מכפתור המשפך בכותרת, מתחת לכותרת; בטלפון — גיליון תחתון): רשימת ערכים ייחודיים עם ספירה
 * וחיפוש ברשימה, ובלשונית שנייה תנאי לפי סוג הנתון. השינויים חלים מיד (onChange(state)).
 * o: {columns, rows, state, fmt, index?, onChange(state), onClose?(key) — להחזרת הפוקוס לכפתור בכותרת}
 * → {el, update(state, shown), open(key, anchor?), close(), openKey(), destroy()}
 */
export function createTableControls(el, o) {
  const doc = el.ownerDocument;
  const win = doc.defaultView;
  const inFrame = !!win && win.parent !== win;
  const id = `abt-${++tseq}`;
  let cols = o.columns;
  let rows = o.rows;
  let state = { q: '', filters: {}, sort: null, ...(o.state || {}) };
  let shown = rows.length;
  let openKey = null;
  let anchor = null;
  let spacer = null;

  const root = tmk(doc, 'div', 'abt');
  const bar = tmk(doc, 'div', 'abt-bar');
  const search = tmk(doc, 'input', 'abt-search');
  search.type = 'search';
  search.placeholder = TABLE_HE.search;
  search.setAttribute('aria-label', TABLE_HE.search);
  search.autocomplete = 'off';
  search.enterKeyHint = 'search';
  search.value = state.q || '';
  const count = tmk(doc, 'span', 'abt-count');
  count.setAttribute('aria-live', 'polite');
  const clear = tbtn(doc, 'abt-clear', TABLE_HE.clearAll);
  bar.append(search, count, clear);
  const chips = tmk(doc, 'div', 'abt-chips');
  const backdrop = tmk(doc, 'div', 'abt-backdrop');
  backdrop.hidden = true;
  backdrop.addEventListener('click', () => close());
  const panel = tmk(doc, 'div', 'abt-panel');
  panel.id = `${id}-panel`;
  panel.setAttribute('role', 'dialog');
  panel.tabIndex = -1;
  panel.hidden = true;
  root.append(bar, chips, backdrop, panel);
  el.appendChild(root);

  const touch = () => !!(win.matchMedia && win.matchMedia('(hover: none)').matches);
  const emit = () => o.onChange && o.onChange({ ...state, filters: { ...state.filters } });
  const setFilter = (key, f) => {
    const filters = { ...state.filters };
    if (f && isActive(f)) filters[key] = f;
    else delete filters[key];
    state = { ...state, filters };
    refresh();
    emit();
    if (openKey === key && panel.clearBtn) panel.clearBtn.disabled = !filters[key];
  };

  const refresh = () => {
    const filtered = isFiltered(state, cols);
    count.textContent = filtered ? TABLE_HE.shownOf(shown, rows.length) : TABLE_HE.rows(rows.length);
    clear.hidden = !filtered;
    chips.textContent = '';
    for (const c of cols) {
      const f = state.filters[c.key];
      if (!isActive(f)) continue;
      const text = chipText(c, f, o.fmt, rows);
      const chip = tmk(doc, 'span', 'ab-chip abt-chip');
      const b = tmk(doc, 'bdi', '', text);
      b.dir = 'auto';
      const x = tbtn(doc, 'ab-chip-x', '×', () => {
        if (openKey === c.key) close();
        setFilter(c.key, null);
        search.focus();
      });
      x.setAttribute('aria-label', TABLE_HE.remove(text));
      chip.append(b, x);
      chips.appendChild(chip);
    }
    chips.hidden = !chips.firstChild;
  };

  const field = (labelText, input) => {
    const lab = tmk(doc, 'label', 'abt-field');
    lab.append(tmk(doc, 'span', 'abt-field-label', labelText), input);
    return lab;
  };
  const input = (type, value) => {
    const inp = tmk(doc, 'input', 'abt-input');
    inp.type = type;
    if (value !== undefined) inp.value = value;
    inp.autocomplete = 'off';
    return inp;
  };
  const select = (opts, value) => {
    const s = tmk(doc, 'select', 'abt-input abt-select');
    for (const [v, t] of opts) {
      const op = tmk(doc, 'option', '', t);
      op.value = v;
      s.appendChild(op);
    }
    s.value = value;
    return s;
  };

  // ── רשימת הערכים (Excel): כולם מסומנים כשאין סינון; חיפוש מצמצם את הרשימה; "בחר הכל" אחרי חיפוש = רק מה שנמצא ──
  const valuesView = (col, box) => {
    const f = state.filters[col.key];
    let sel = f && Array.isArray(f.in) ? new Set(f.in) : null; // null = הכל
    let q = '';
    let order = 'value';
    let limit = LIST_PAGE;
    const expanded = new Set();
    let data = distinctValues(rows, col, { cols, state, fmt: o.fmt, index: o.index });
    const norms = new Map();
    const normOf = (it) => {
      let s = norms.get(it.value);
      if (s === undefined) {
        s = normalize(it.labelHe);
        norms.set(it.value, s);
      }
      return s;
    };
    const allKeys = () => data.items.map((it) => it.value);
    const isSel = (k) => (sel ? sel.has(k) : true);
    const found = () => {
      const t = normalize(q);
      return t ? data.items.filter((it) => normOf(it).includes(t)) : data.items;
    };
    const apply = () => {
      setFilter(col.key, selectionFilter(sel, allKeys()));
      status();
    };
    const setMany = (keys, on) => {
      if (keys.length === 1) sel = pickValues(sel, allKeys(), null, { key: keys[0], on });
      else {
        sel = new Set(sel || allKeys());
        for (const k of keys) {
          if (on) sel.add(k);
          else sel.delete(k);
        }
      }
      apply();
    };
    const searching = () => (normalize(q) ? found().map((it) => it.value) : null);

    const find = input('search');
    find.placeholder = TABLE_HE.findValue;
    find.setAttribute('aria-label', TABLE_HE.findValue);
    find.classList.add('abt-find');
    const tools = tmk(doc, 'div', 'abt-tools');
    const all = tbtn(doc, 'abt-link', TABLE_HE.selectAll, () => {
      sel = pickValues(sel, allKeys(), searching(), 'all');
      apply();
      renderList();
    });
    const none = tbtn(doc, 'abt-link', TABLE_HE.clear, () => {
      sel = pickValues(sel, allKeys(), searching(), 'none');
      apply();
      renderList();
    });
    const info = tmk(doc, 'span', 'abt-sel-n');
    info.setAttribute('aria-live', 'polite');
    const ord = tbtn(doc, 'abt-order', TABLE_HE.byCount, () => {
      order = order === 'value' ? 'count' : 'value';
      ord.textContent = order === 'value' ? TABLE_HE.byCount : TABLE_HE.byValue;
      data = distinctValues(rows, col, { cols, state: { ...state }, fmt: o.fmt, index: o.index, order });
      renderList();
    });
    ord.title = TABLE_HE.orderBy;
    tools.append(all, none, info, ord);
    const list = tmk(doc, 'div', 'abt-opts');
    list.id = `${id}-list`;
    list.setAttribute('role', 'group');
    list.setAttribute('aria-label', TABLE_HE.tabValues);
    box.append(find, tools, list);

    function status() {
      const total = data.items.length;
      const n = sel ? data.items.reduce((s, it) => s + (sel.has(it.value) ? 1 : 0), 0) : total;
      const qn = normalize(q) ? found().length : null;
      info.textContent = qn !== null ? TABLE_HE.found(qn) : n === total ? TABLE_HE.values(total) : TABLE_HE.selectedOf(n, total);
      all.textContent = normalize(q) ? TABLE_HE.selectFound : TABLE_HE.selectAll;
      for (const g of list.querySelectorAll('input[data-keys]')) {
        const keys = g.abKeys;
        const on = keys.filter((k) => isSel(k)).length;
        g.checked = on === keys.length;
        g.indeterminate = on > 0 && on < keys.length;
      }
    }

    const optRow = (it, depth) => {
      const lab = tmk(doc, 'label', 'abt-opt');
      if (depth) lab.style.paddingInlineStart = `${6 + depth * 22}px`;
      const cb = tmk(doc, 'input');
      cb.type = 'checkbox';
      cb.checked = isSel(it.value);
      cb.addEventListener('change', () => {
        setMany([it.value], cb.checked);
        status();
      });
      const name = tmk(doc, 'bdi', 'abt-opt-name', it.labelHe);
      name.dir = 'auto';
      if (it.value === '') name.classList.add('abt-opt-empty');
      lab.append(cb, name, tmk(doc, 'span', 'abt-opt-n', it.count.toLocaleString('he-IL')));
      return lab;
    };
    const groupRow = (g, keys, depth, labelText) => {
      const row = tmk(doc, 'div', 'abt-opt abt-grp');
      if (depth) row.style.paddingInlineStart = `${6 + depth * 22}px`;
      const open = expanded.has(g.key);
      const tw = tbtn(doc, 'abt-tw', '', () => {
        if (expanded.has(g.key)) expanded.delete(g.key);
        else expanded.add(g.key);
        renderList(g.key);
      });
      tw.setAttribute('aria-expanded', open ? 'true' : 'false');
      tw.setAttribute('aria-label', TABLE_HE.expand(labelText));
      tw.tabIndex = -1;
      const lab = tmk(doc, 'label', 'abt-grp-lab');
      const cb = tmk(doc, 'input');
      cb.type = 'checkbox';
      cb.dataset.keys = '1';
      cb.dataset.grp = g.key;
      cb.abKeys = keys;
      cb.addEventListener('change', () => {
        setMany(keys, cb.checked);
        renderList(g.key);
      });
      const name = tmk(doc, 'bdi', 'abt-opt-name', labelText);
      lab.append(cb, name, tmk(doc, 'span', 'abt-opt-n', g.count.toLocaleString('he-IL')));
      row.append(tw, lab);
      return row;
    };

    function renderList(focusGroup) {
      list.textContent = '';
      const frag = doc.createDocumentFragment();
      const items = found();
      const tree = data.kind === 'date' && !normalize(q) && order === 'value' && items.length > 15;
      if (!items.length) frag.appendChild(tmk(doc, 'p', 'abt-none', TABLE_HE.noValues));
      else if (tree) {
        const { years, rest } = dateTree(items);
        if (!expanded.size && years.length) {
          const last = years[years.length - 1];
          expanded.add(last.key);
          if (last.months.length === 1) expanded.add(last.months[0].key);
        }
        for (const y of years) {
          const ykeys = y.months.flatMap((m) => m.items.map((it) => it.value));
          frag.appendChild(groupRow(y, ykeys, 0, y.labelHe));
          if (!expanded.has(y.key)) continue;
          for (const m of y.months) {
            frag.appendChild(groupRow(m, m.items.map((it) => it.value), 1, m.labelHe));
            if (expanded.has(m.key)) for (const it of m.items) frag.appendChild(optRow(it, 2));
          }
        }
        for (const it of rest) frag.appendChild(optRow(it, 0));
      } else {
        for (const it of items.slice(0, limit)) frag.appendChild(optRow(it, 0));
        if (items.length > limit) {
          const more = tmk(doc, 'div', 'abt-more');
          more.append(
            tmk(doc, 'span', '', TABLE_HE.more(items.length - limit)),
            tbtn(doc, 'abt-link', TABLE_HE.showMore, () => {
              limit += LIST_PAGE;
              renderList();
            }),
          );
          frag.appendChild(more);
        }
      }
      list.appendChild(frag);
      status();
      if (focusGroup) {
        const g = list.querySelector(`input[data-grp="${focusGroup}"]`);
        if (g) g.focus();
      }
    }

    find.addEventListener('input', () => {
      q = find.value;
      limit = LIST_PAGE;
      renderList();
    });
    find.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        const first = list.querySelector('input');
        if (first) {
          e.preventDefault();
          first.focus();
        }
      } else if (e.key === 'Enter') {
        // Excel: Enter אחרי חיפוש = רק הערכים שנמצאו
        e.preventDefault();
        const keys = searching();
        if (keys && keys.length) {
          sel = pickValues(sel, allKeys(), keys, 'all');
          apply();
        }
        close();
      }
    });
    list.addEventListener('keydown', (e) => {
      const boxes = [...list.querySelectorAll('input')];
      const i = boxes.indexOf(doc.activeElement);
      if (i < 0) return;
      const rtl = win.getComputedStyle(list).direction === 'rtl';
      const go = (j) => {
        e.preventDefault();
        if (j < 0) find.focus();
        else if (boxes[Math.min(j, boxes.length - 1)]) boxes[Math.min(j, boxes.length - 1)].focus();
      };
      if (e.key === 'ArrowDown') go(i + 1);
      else if (e.key === 'ArrowUp') go(i - 1);
      else if (e.key === 'Home') go(0);
      else if (e.key === 'End') go(boxes.length - 1);
      else if (e.key === 'Enter') {
        e.preventDefault();
        close();
      } else if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && boxes[i].dataset.grp) {
        const k = boxes[i].dataset.grp;
        const inward = (e.key === 'ArrowLeft') === rtl;
        e.preventDefault();
        if (inward !== expanded.has(k)) {
          if (inward) expanded.add(k);
          else expanded.delete(k);
          renderList(k);
        }
      }
    });
    renderList();
    return find;
  };

  // ── תנאי לפי סוג: מספר (בין/=/≠/>/</≥/≤), תאריך (קיצורים + מ-עד), טקסט (מכיל/מתחיל ב/לא מכיל) ──
  const condView = (col, kind, box) => {
    const f = state.filters[col.key] || {};
    const done = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        close();
      }
    };
    if (kind === 'text') {
      const op = select(
        [
          ['contains', TABLE_HE.contains],
          ['starts', TABLE_HE.starts],
          ['not', TABLE_HE.notContains],
        ],
        f.op || 'contains',
      );
      const inp = input('search', typeof f.text === 'string' ? f.text : '');
      const upd = () => setFilter(col.key, { text: inp.value, op: op.value });
      op.addEventListener('change', upd);
      inp.addEventListener('input', upd);
      inp.addEventListener('keydown', done);
      box.append(field(TABLE_HE.condition, op), field(TABLE_HE.value, inp));
      return op;
    }
    if (kind === 'num') {
      const cur = 'cmp' in f ? f.cmp : isNum(f.min) && isNum(f.max) ? 'between' : isNum(f.min) ? 'ge' : isNum(f.max) ? 'le' : 'between';
      const op = select(Object.entries(TABLE_HE.ops), cur);
      const a = input('text');
      const b = input('text');
      for (const x of [a, b]) {
        x.inputMode = 'decimal';
        x.dir = 'ltr';
        x.addEventListener('keydown', done);
      }
      const str = (n) => (isNum(n) ? String(n) : '');
      a.value = 'cmp' in f ? str(f.n) : cur === 'le' ? str(f.max) : str(f.min);
      b.value = cur === 'between' ? str(f.max) : '';
      const unit = col.type === 'percent' ? ' (%)' : '';
      const fa = field(TABLE_HE.value + unit, a);
      const fb = field(TABLE_HE.to + unit, b);
      const r = numRange(rows, col);
      if (r) {
        a.placeholder = NF.format(r.min);
        b.placeholder = NF.format(r.max);
      }
      const shape = () => {
        const between = op.value === 'between';
        fb.hidden = !between;
        fa.firstChild.textContent = (between ? TABLE_HE.from : TABLE_HE.value) + unit;
      };
      const upd = () => {
        shape();
        const x = inputNum(a.value);
        const v = op.value;
        if (v === 'between') setFilter(col.key, { min: x, max: inputNum(b.value) });
        else if (v === 'ge') setFilter(col.key, { min: x });
        else if (v === 'le') setFilter(col.key, { max: x });
        else setFilter(col.key, { cmp: v, n: x });
      };
      op.addEventListener('change', upd);
      a.addEventListener('input', upd);
      b.addEventListener('input', upd);
      shape();
      const row = tmk(doc, 'div', 'abt-row');
      row.append(fa, fb);
      box.append(field(TABLE_HE.condition, op), row);
      if (r) box.appendChild(tmk(doc, 'p', 'abt-hint', TABLE_HE.rangeHint(NF.format(r.min), NF.format(r.max))));
      return op;
    }
    // תאריכים
    const pres = tmk(doc, 'div', 'abt-presets');
    pres.setAttribute('role', 'group');
    pres.setAttribute('aria-label', TABLE_HE.tabCond.date);
    const lo = input('date', f.preset ? '' : f.from || '');
    const hi = input('date', f.preset ? '' : f.to || '');
    const btns = [];
    const mark = (p) => {
      for (const b of btns) b.setAttribute('aria-pressed', b.dataset.p === p ? 'true' : 'false');
    };
    for (const p of DATE_PRESETS) {
      const b = tbtn(doc, 'ab-chip abt-preset', TABLE_HE.presets[p], () => {
        const on = b.getAttribute('aria-pressed') !== 'true';
        const r = presetRange(p);
        lo.value = on ? r.from : '';
        hi.value = on ? r.to : '';
        mark(on ? p : null);
        setFilter(col.key, on ? { preset: p } : null);
      });
      b.dataset.p = p;
      btns.push(b);
      pres.appendChild(b);
    }
    if (f.preset) {
      const r = presetRange(f.preset);
      if (r) {
        lo.value = r.from;
        hi.value = r.to;
      }
    }
    mark(f.preset || null);
    const upd = () => {
      mark(null);
      setFilter(col.key, { from: lo.value || null, to: hi.value || null });
    };
    for (const x of [lo, hi]) {
      x.addEventListener('input', upd);
      x.addEventListener('keydown', done);
    }
    const row = tmk(doc, 'div', 'abt-row');
    row.append(field(TABLE_HE.from, lo), field(TABLE_HE.to, hi));
    box.append(pres, row);
    return btns[0];
  };

  const buildPanel = (col) => {
    panel.textContent = '';
    const kind = kindOf(col, rows);
    const cond = kind === 'bool' ? null : kind;
    const f = state.filters[col.key];
    const title = TABLE_HE.filterBy(col.labelHe || col.key);
    panel.setAttribute('aria-label', title);
    panel.dataset.kind = kind;
    const head = tmk(doc, 'div', 'abt-panel-head');
    head.appendChild(tmk(doc, 'strong', '', title));
    const x = tbtn(doc, 'abt-x', '×', () => close());
    x.setAttribute('aria-label', TABLE_HE.close);
    head.appendChild(x);
    panel.appendChild(head);
    const body = tmk(doc, 'div', 'abt-panel-body');
    let tab = f && isActive(f) && !Array.isArray(f.in) && cond ? 'cond' : 'values';
    let focusEl = null;
    const tabs = cond ? tmk(doc, 'div', 'ab-seg abt-tabs') : null;
    const show = () => {
      body.textContent = '';
      body.className = `abt-panel-body abt-${tab}`;
      focusEl = tab === 'values' ? valuesView(col, body) : condView(col, cond, body);
      if (tabs) for (const b of tabs.children) b.setAttribute('aria-pressed', b.dataset.tab === tab ? 'true' : 'false');
    };
    if (tabs) {
      for (const [t, label] of [
        ['values', TABLE_HE.tabValues],
        ['cond', TABLE_HE.tabCond[cond]],
      ]) {
        const b = tbtn(doc, '', label, () => {
          if (tab === t) return;
          tab = t;
          show();
          if (focusEl && !touch()) focusEl.focus();
        });
        b.dataset.tab = t;
        tabs.appendChild(b);
      }
      panel.appendChild(tabs);
    }
    panel.appendChild(body);
    const foot = tmk(doc, 'div', 'abt-panel-foot');
    const clr = tbtn(doc, 'abt-link', TABLE_HE.clearFilter, () => {
      setFilter(col.key, null);
      show();
      if (focusEl && !touch()) focusEl.focus();
    });
    clr.disabled = !isActive(f);
    panel.clearBtn = clr;
    foot.append(clr, tbtn(doc, 'ab-btn abt-done', TABLE_HE.done, () => close()));
    panel.appendChild(foot);
    show();
    return () => focusEl;
  };

  // מיקום: מתחת לכפתור בכותרת, בתוך רוחב הטבלה; בטלפון (לא ב-iframe — שם אין "תחתית מסך") גיליון תחתון
  const place = () => {
    if (openKey === null) return;
    const narrow = win.innerWidth < 768;
    const sheet = narrow && !inFrame;
    panel.classList.toggle('abt-sheet', sheet);
    backdrop.hidden = !sheet;
    panel.style.insetInlineStart = '';
    panel.style.insetBlockStart = '';
    if (sheet) return;
    const rr = root.getBoundingClientRect();
    const ar = anchor && anchor.isConnected ? anchor.getBoundingClientRect() : rr;
    const rtl = win.getComputedStyle(root).direction === 'rtl';
    const w = panel.offsetWidth;
    const start = rtl ? rr.right - ar.right : ar.left - rr.left;
    panel.style.insetInlineStart = `${Math.max(0, Math.min(start, rr.width - w))}px`;
    panel.style.insetBlockStart = `${(anchor ? ar.bottom : rr.bottom) - rr.top + 6}px`;
    if (inFrame) {
      // גובה ה-iframe נקבע לפי תחתית ה-body — מוסיפים מקום כדי שהלוח לא ייחתך
      if (!spacer) {
        spacer = tmk(doc, 'div', 'abt-spacer');
        doc.body.appendChild(spacer);
      }
      spacer.style.blockSize = '0px';
      const over = panel.getBoundingClientRect().bottom - doc.body.getBoundingClientRect().bottom;
      spacer.style.blockSize = `${Math.max(0, Math.ceil(over) + 12)}px`;
    }
  };
  const onDown = (e) => {
    if (openKey === null) return;
    const t = e.target;
    if (panel.contains(t) || (anchor && anchor.contains(t))) return;
    close(false);
  };
  const onResize = () => place();
  const onScroll = (e) => {
    if (openKey !== null && e.target !== doc && !panel.contains(e.target) && e.target.contains && anchor && e.target.contains(anchor)) place();
  };

  panel.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  });

  function open(key, anchorEl) {
    const col = cols.find((c) => c.key === key);
    if (!col) return;
    if (openKey !== null) close(false, true);
    openKey = key;
    anchor = anchorEl || null;
    const focusOf = buildPanel(col);
    panel.hidden = false;
    place();
    doc.addEventListener('pointerdown', onDown, true);
    win.addEventListener('resize', onResize);
    doc.addEventListener('scroll', onScroll, true);
    // במגע — בלי להקפיץ מקלדת: הפוקוס על הלוח עצמו
    const first = touch() ? panel : focusOf() || panel.querySelector('input, select, button');
    if (first) first.focus({ preventScroll: true });
  }

  function close(refocus = true, silent = false) {
    if (openKey === null) return;
    const key = openKey;
    openKey = null;
    anchor = null;
    panel.hidden = true;
    backdrop.hidden = true;
    panel.textContent = '';
    panel.clearBtn = null;
    if (spacer) {
      spacer.remove();
      spacer = null;
    }
    doc.removeEventListener('pointerdown', onDown, true);
    win.removeEventListener('resize', onResize);
    doc.removeEventListener('scroll', onScroll, true);
    if (o.onClose && !silent) o.onClose(key, refocus);
  }

  search.addEventListener('input', () => {
    state = { ...state, q: search.value };
    refresh();
    emit();
  });
  search.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && search.value) {
      e.preventDefault();
      search.value = '';
      state = { ...state, q: '' };
      refresh();
      emit();
    }
  });
  clear.addEventListener('click', () => {
    close(false);
    search.value = '';
    state = { ...state, q: '', filters: {} };
    refresh();
    emit();
    search.focus();
  });

  refresh();
  return {
    el: root,
    /** מצב חדש מבחוץ (מיון, איפוס) + כמה שורות בתצוגה; columns/rows — כשהנתונים התחלפו. */
    update(next, nShown, data) {
      if (data) {
        cols = data.columns || cols;
        rows = data.rows || rows;
        if (data.index !== undefined) o.index = data.index;
      }
      if (next) state = { q: '', filters: {}, sort: null, ...next };
      if (typeof nShown === 'number') shown = nShown;
      if (search.value !== (state.q || '') && doc.activeElement !== search) search.value = state.q || '';
      refresh();
    },
    open,
    close: () => close(),
    openKey: () => openKey,
    /** הכפתור בכותרת נבנה מחדש (ab.table מצייר את הכותרת בכל שינוי) — הלוח נשאר מעוגן אליו. */
    setAnchor(a) {
      if (openKey === null || !a) return;
      anchor = a;
      place();
    },
    destroy() {
      close(false, true);
      root.remove();
    },
  };
}

/**
 * כפתורי הכותרת (DOM): שם העמודה הוא כפתור מיון (החץ ב-CSS לפי aria-sort של ה-th — לא נכנס לטקסט), ולידו משפך סינון.
 */
function headerCell(doc, th, col, h) {
  const wrap = tmk(doc, 'span', 'abt-th');
  const sortBtn = tbtn(doc, 'abt-sort', col.labelHe || col.key, () => h.sort(col));
  wrap.appendChild(sortBtn);
  let fbtn = null;
  if (h.filters) {
    fbtn = tbtn(doc, 'abt-fbtn', undefined, () => h.filter(col, fbtn));
    fbtn.setAttribute('aria-label', TABLE_HE.filterBy(col.labelHe || col.key));
    fbtn.setAttribute('aria-haspopup', 'dialog');
    wrap.appendChild(fbtn);
  }
  th.appendChild(wrap);
  return { sortBtn, fbtn };
}

/**
 * ab.table (iframe של בלוק app): טבלה מעוצבת עם מיון בלחיצה על כותרת (עולה/יורד/בלי), חיפוש וסינון חכם לכל עמודה
 * (מ-CONTROLS_MIN_ROWS שורות, או filters:true/false), שורת סה"כ על השורות שבתצוגה, אירועי שורה. סוג עמודה בלי type — מהערכים.
 * o: {columns:[{key, labelHe, type?, swatch?, bar?, render?}], rows, sort?, filters?, totals?, onRowEnter?, onRowLeave?,
 *     onRowClick?, rowClass?, maxHeight?, onView?(rows)} ; deps: {fmt}
 * → {el, rows(), view(), setRows(rows), refresh(), highlight(i|null), clear(), destroy()}
 */
export function createTable(host, o, deps) {
  const doc = host.ownerDocument;
  const fmt = deps.fmt;
  let rows = (o.rows || []).slice();
  let index = null;
  let state = { q: '', filters: {}, sort: o.sort || null };
  let hl = null;
  let view = [];
  const cols = o.columns || [];
  const root = tmk(doc, 'div', 'abt-host');
  const wrap = tmk(doc, 'div', 'ab-table-wrap');
  if (o.maxHeight) wrap.style.maxHeight = typeof o.maxHeight === 'number' ? `${o.maxHeight}px` : o.maxHeight;
  const table = tmk(doc, 'table', 'ab-table');
  wrap.appendChild(table);
  const useControls = () => (o.filters === undefined ? rows.length >= CONTROLS_MIN_ROWS : !!o.filters);
  let ctrl = null;
  const fbtns = {};
  const ctrlHost = tmk(doc, 'div');
  root.append(ctrlHost, wrap);
  host.appendChild(root);
  // יישור: align מפורש ('end' = צד המספרים, שמאל ב-RTL; 'start' = עם הטקסט) גובר; אחרת מספר → end (באג 8a164130)
  const numeric = (c) => (c.align ? c.align === 'end' : kindOf(c, rows) === 'num');
  const cellText = (c, v) => (c.type ? fmt(v, c.type) : v == null ? '–' : String(v));

  const ensureControls = () => {
    if (useControls() && !ctrl) {
      ctrl = createTableControls(ctrlHost, {
        columns: cols,
        rows,
        state,
        fmt,
        onChange: (s) => {
          state = { ...s, sort: state.sort };
          renderBody();
        },
        onClose: (key, refocus) => {
          const b = fbtns[key];
          if (b) {
            b.setAttribute('aria-expanded', 'false');
            if (refocus !== false) b.focus();
          }
        },
      });
    } else if (!useControls() && ctrl) {
      ctrl.destroy();
      ctrl = null;
      state = { ...state, q: '', filters: {} };
    }
  };

  const renderHead = () => {
    const open = ctrl && ctrl.openKey();
    let anchor = null;
    const old = table.tHead;
    if (old) old.remove();
    const thead = table.createTHead();
    const hr = thead.insertRow();
    for (const c of cols) {
      const th = tmk(doc, 'th');
      th.scope = 'col';
      if (numeric(c)) th.className = 'ab-num';
      if (state.sort && state.sort.key === c.key) th.setAttribute('aria-sort', state.sort.dir === 'asc' ? 'ascending' : 'descending');
      const active = isActive(state.filters[c.key]);
      if (active) th.dataset.filtered = 'true';
      const { fbtn } = headerCell(doc, th, c, {
        filters: !!ctrl,
        sort: (col) => {
          state = { ...state, sort: nextSort(state.sort, col, rows) };
          renderBody();
          const b = table.tHead && [...table.tHead.querySelectorAll('.abt-sort')][cols.indexOf(col)];
          if (b) b.focus();
        },
        filter: (col, btn) => {
          if (!ctrl) return;
          if (ctrl.openKey() === col.key) ctrl.close();
          else {
            for (const k in fbtns) fbtns[k].setAttribute('aria-expanded', 'false');
            btn.setAttribute('aria-expanded', 'true');
            ctrl.open(col.key, btn);
          }
        },
      });
      if (fbtn) {
        fbtns[c.key] = fbtn;
        fbtn.setAttribute('aria-expanded', open === c.key ? 'true' : 'false');
        if (active) fbtn.dataset.active = 'true';
        if (open === c.key) anchor = fbtn;
      }
      hr.appendChild(th);
    }
    return anchor;
  };

  const renderBody = () => {
    if (ctrl || isFiltered(state, cols)) index = index || searchIndex(rows, cols, fmt);
    view = viewIdx(rows, cols, state, fmt, index);
    const anchor = renderHead();
    if (ctrl && anchor) ctrl.setAnchor(anchor);
    for (const tb of [...table.tBodies]) tb.remove();
    if (table.tFoot) table.tFoot.remove();
    const maxOf = {};
    for (const c of cols) {
      if (!c.bar) continue;
      maxOf[c.key] = rows.reduce((m, r) => {
        const n = parseNumber(r[c.key]);
        return n !== null && Math.abs(n) > m ? Math.abs(n) : m;
      }, 0);
    }
    const tb = table.createTBody();
    for (const i of view) {
      const row = rows[i];
      const tr = tb.insertRow();
      tr.dataset.i = String(i);
      const cls = o.rowClass ? o.rowClass(row, i) : '';
      if (cls) tr.className = cls;
      if (hl === i) tr.classList.add('ab-hl');
      for (const c of cols) {
        const td = tr.insertCell();
        if (numeric(c)) td.className = 'ab-num';
        if (c.swatch) {
          const s = tmk(doc, 'span', 'ab-swatch');
          s.style.background = c.swatch(row, i);
          td.appendChild(s);
        }
        const out = c.render ? c.render(row[c.key], row, i) : cellText(c, row[c.key]);
        if (out && typeof out === 'object' && out.nodeType) td.appendChild(out);
        else {
          const b = tmk(doc, 'bdi', '', out == null ? '' : String(out));
          td.appendChild(b);
        }
        if (c.bar && maxOf[c.key]) {
          const n = parseNumber(row[c.key]);
          const bar = tmk(doc, 'span', 'ab-cell-bar');
          bar.style.inlineSize = `${n === null ? 0 : (Math.abs(n) / maxOf[c.key]) * 100}%`;
          if (typeof c.bar === 'string') bar.style.background = c.bar;
          td.classList.add('ab-has-bar');
          td.appendChild(bar);
        }
      }
      if (o.onRowEnter) tr.addEventListener('mouseenter', () => o.onRowEnter(row, i));
      if (o.onRowLeave) tr.addEventListener('mouseleave', () => o.onRowLeave(row, i));
      if (o.onRowClick) {
        tr.style.cursor = 'pointer';
        tr.addEventListener('click', () => o.onRowClick(row, i));
      }
    }
    if (!view.length && rows.length) {
      const td = tb.insertRow().insertCell();
      td.colSpan = cols.length || 1;
      td.className = 'abt-nomatch';
      td.textContent = TABLE_HE.noMatch;
    }
    if (o.totals) {
      const sums = columnTotals(view.map((i) => rows[i]), cols);
      const tf = table.createTFoot().insertRow();
      tf.className = 'ab-total';
      cols.forEach((c, ci) => {
        const td = tf.insertCell();
        if (c.key in sums) {
          if (numeric(c)) td.className = 'ab-num';
          td.textContent = fmt(sums[c.key], c.type);
        } else if (ci === 0) td.textContent = (o.totals && o.totals.labelHe) || TABLE_HE.total;
      });
    }
    if (ctrl) ctrl.update(state, view.length);
    if (o.onView) o.onView(view.map((i) => rows[i]));
  };

  ensureControls();
  renderBody();
  return {
    el: table,
    rows: () => rows,
    /** השורות שבתצוגה — אחרי חיפוש, סינון ומיון. */
    view: () => view.map((i) => rows[i]),
    setRows(r) {
      rows = (r || []).slice();
      index = null;
      hl = null;
      if (ctrl) ctrl.close();
      ensureControls();
      if (ctrl) ctrl.update(null, undefined, { columns: cols, rows });
      renderBody();
    },
    refresh: () => renderBody(),
    /** מסמן שורה (אינדקס במערך המקורי) — למשל כשעוברים על הנקודה שלה בגרף; null מנקה. */
    highlight(i) {
      hl = i === null || i === undefined ? null : i;
      for (const tr of table.tBodies[0] ? table.tBodies[0].rows : []) tr.classList.toggle('ab-hl', String(hl) === tr.dataset.i);
    },
    /** מנקה חיפוש וסינון (המיון נשאר). */
    clear() {
      if (ctrl) ctrl.close();
      state = { ...state, q: '', filters: {} };
      renderBody();
    },
    destroy: () => {
      if (ctrl) ctrl.destroy();
      root.remove();
    },
  };
}
